<template>
  <div class="automation-settings">
    <div class="automation-setting-header">
      <div class="header">
        <span class="automation-settings-title">{{ $t(`${i18nPrefix}.label.automationSettingsTitle`) }}</span>
        <el-switch v-model="enabledConfig" active-color="#0096cc" class="need-config-switch" :style="isEn ? 'margin-left: 23px;' : 'margin-left: 28px;'"></el-switch>
      </div>
      <el-select
        class="company-select"
        size="mini"
        filterable
        v-model="companyId"
        :filter-method="handleFilterCompany"
        @visible-change="handleVisibilityChange"
        @change="searchCompanyList">
        <el-option
          v-for="(company, index) in searchedCompanyList"
          :key="index"
          :label="company.i18nName"
          :value="company.id"
          :style="handleCompanySelectStyle(company)"
        ></el-option>
      </el-select>
    </div>
    <el-divider class="divider"></el-divider>
    <div class="settings-container job-settings-container">
      <span class="title job-settings-title">{{ $t(`${i18nPrefix}.label.jobSettings.jobSettingTitle`) }}</span>
      <div class="job-close-settings">
        <div class="title">
          <span class="title-content">{{ $t(`${i18nPrefix}.label.jobSettings.jobCloseSettingTitle`) }}</span>
          <el-switch
            v-model="jobSettings.job_close_setting_is_enabled"
            class="switch"
            :disabled="!enabledConfig"
          ></el-switch>
        </div>
        <div class="job-close-content">
          <div class="content-prev" :style="isEn ? 'margin-right: 34px;' : ''">{{ $t(`${i18nPrefix}.label.jobSettings.jobClosePrevContent`) }}</div>
          <el-select class="email-template-select" size="mini" v-model="jobSettings.job_close_email_template" :disabled="!enabledConfig">
            <el-option
              v-for="(emailTemplate, index) in jobCloseEmailTemplateList"
              :key="index"
              :label="emailTemplate.name"
              :value="emailTemplate.id"
            ></el-option>
          </el-select>
          <div class="email-to">{{ $t(`${i18nPrefix}.label.jobSettings.jobSettingsSendTo`) }}</div>
          <el-select class="status-step-select" size="mini" v-model="jobSettings.job_close_status" :disabled="!enabledConfig">
            <el-option
              v-for="(status, index) in candidateStatusList"
              :key="index"
              :label="status.i18nName"
              :value="status.key"
            ></el-option>
          </el-select>
          <div class="content-end">{{ $t(`${i18nPrefix}.label.jobSettings.jobSettingsEnd`) }}</div>
        </div>
      </div>
      <div class="job-cancel-settings">
        <div class="title">
          <span class="title-content">{{ $t(`${i18nPrefix}.label.jobSettings.jobCancelSettingTitle`) }}</span>
          <el-switch
            v-model="jobSettings.job_cancel_setting_is_enabled"
            class="switch"
            :disabled="!enabledConfig"
          ></el-switch>
        </div>
        <div class="job-cancel-content">
          <div class="content-prev">{{ $t(`${i18nPrefix}.label.jobSettings.jobCancelPrevContent`) }}</div>
          <el-select class="email-template-select" size="mini" v-model="jobSettings.job_cancel_email_template" :disabled="!enabledConfig">
            <el-option
              v-for="(emailTemplate, index) in jobCancelEmailTemplateList"
              :key="index"
              :label="emailTemplate.name"
              :value="emailTemplate.id"
            ></el-option>
          </el-select>
          <div class="email-to">{{ $t(`${i18nPrefix}.label.jobSettings.jobSettingsSendTo`) }}</div>
          <el-select class="status-step-select" size="mini" v-model="jobSettings.job_cancel_status" :disabled="!enabledConfig">
            <el-option
              v-for="(status, index) in candidateStatusList"
              :key="index"
              :label="status.i18nName"
              :value="status.key"
            ></el-option>
          </el-select>
          <div class="content-end">{{ $t(`${i18nPrefix}.label.jobSettings.jobSettingsEnd`) }}</div>
        </div>
      </div>
      <div class="job-close-application-status-settings">
        <div class="title">
          <span class="title-content">{{ $t(`${i18nPrefix}.label.jobSettings.jobCloseApplicationStatusTitle`) }}</span>
          <el-switch
            v-model="jobSettings.job_close_application_status_change.is_enabled"
            class="switch"
            :disabled="!enabledConfig"
          ></el-switch>
        </div>
        <div class="job-close-application-status-content">
          <span class="content-label">{{ $t(`${i18nPrefix}.label.jobSettings.jobCloseApplicationSourceStatus`) }}</span>
          <el-select
            v-model="jobCloseApplicationSourceStatusSelection"
            class="application-status-select source-status-select"
            size="mini"
            multiple
            filterable
            clearable
            :disabled="!enabledConfig || !jobSettings.job_close_application_status_change.is_enabled"
          >
            <el-option
              v-for="status in jobCloseApplicationSourceStatusOptions"
              :key="status.key"
              :label="status.i18nName"
              :value="status.key"
            ></el-option>
          </el-select>
          <span class="content-label target-status-label">{{ $t(`${i18nPrefix}.label.jobSettings.jobCloseApplicationTargetStatus`) }}</span>
          <el-select
            v-model="jobSettings.job_close_application_status_change.target_status_key"
            class="application-status-select target-status-select"
            size="mini"
            filterable
            clearable
            :disabled="!enabledConfig || !jobSettings.job_close_application_status_change.is_enabled"
          >
            <el-option
              v-for="status in jobCloseApplicationTargetStatusOptions"
              :key="status.key"
              :label="status.i18nName"
              :value="status.key"
            ></el-option>
          </el-select>
        </div>
        <div class="job-close-application-status-settings">
          <div class="title">
            <span class="title-content">{{ $t(`${i18nPrefix}.label.jobSettings.jobCancelApplicationStatusTitle`) }}</span>
            <el-switch
              v-model="jobSettings.job_cancel_application_status_change.is_enabled"
              class="switch"
              :disabled="!enabledConfig"
            ></el-switch>
          </div>
        </div>
        <div class="job-close-application-status-content">
          <span class="content-label">{{ $t(`${i18nPrefix}.label.jobSettings.jobCancelApplicationSourceStatus`) }}</span>
          <el-select
            v-model="jobCancelApplicationSourceStatusSelection"
            class="application-status-select source-status-select"
            size="mini"
            multiple
            filterable
            clearable
            :disabled="!enabledConfig || !jobSettings.job_cancel_application_status_change.is_enabled"
          >
            <el-option
              v-for="status in jobCancelApplicationSourceStatusOptions"
              :key="status.key"
              :label="status.i18nName"
              :value="status.key"
            ></el-option>
          </el-select>
          <span class="content-label target-status-label">{{ $t(`${i18nPrefix}.label.jobSettings.jobCancelApplicationTargetStatus`) }}</span>
          <el-select
            v-model="jobSettings.job_cancel_application_status_change.target_status_key"
            class="application-status-select target-status-select"
            size="mini"
            filterable
            clearable
            :disabled="!enabledConfig || !jobSettings.job_cancel_application_status_change.is_enabled"
          >
            <el-option
              v-for="status in jobCancelApplicationTargetStatusOptions"
              :key="status.key"
              :label="status.i18nName"
              :value="status.key"
            ></el-option>
          </el-select>
        </div>
      </div>
    </div>
    <div class="settings-container company-policy-settings-container">
      <span class="title company-policy-settings-title">{{ $t(`${i18nPrefix}.label.companyPolicySettings`) }}</span>
      <div class="revoke-email-address">
        <div class="email-title">
          <div class="title">{{ $t(`${i18nPrefix}.label.revokeEmailAddress`) }}</div>
          <el-input class="email-input" v-model="companyPolicySettings.revoked_notice_email" @blur="validateEmailAddress" :disabled="!enabledConfig || !hasPolicyPermission"></el-input>
          <span v-if="!isValidateEmail" class="validate-required">{{ errMsg }}</span>
        </div>
        <div class="switch-send-candidate">
          <span class="switch-text">{{ $t(`${i18nPrefix}.label.revokeNoticeCandidateTitle`) }}</span>
          <el-switch v-model="companyPolicySettings.revoked_need_notice_candidate" active-color="#0096CC" :disabled="!enabledConfig || !hasPolicyPermission"></el-switch>
        </div>
      </div>
    </div>
    <div class="settings-container interview-satisfaction-settings-container">
      <span v-if="hasSatisfactionSurveyPremium" class="title interview-satisfaction-settings-title">{{ $t(`${i18nPrefix}.label.interviewSatisfactionTitle`) }}</span>
      <div v-if="hasSatisfactionSurveyPremium" class="interview-satisfaction-settings">
        <div class="settings-prev">{{ $t(`${i18nPrefix}.label.interviewSatisfactionSetting`) }}</div>
          <el-select class="interview-satisfaction-setting-select" clearable size="mini" v-model="interviewSatisfactionSurveySettings.interview_satisfaction_survey_send_type" :disabled="!enabledConfig || !hasSatisfactionSurveyPermission">
            <el-option
              v-for="(item, index) in interviewSettingOption"
              :key="index"
              :label="item.label"
              :value="item.value"
            ></el-option>
          </el-select>
      </div>
    </div>
      <div class="settings-container result-status-automation-settings-container">
        <span class="title">{{ $t(`${i18nPrefix}.label.resultStatusAutomation.title`) }}</span>
        <div class="result-status-automation-list">
          <div class="result-status-item">
            <span class="result-status-text">{{ $t(`${i18nPrefix}.label.resultStatusAutomation.hmRatingEnabled`) }}</span>
            <el-switch v-model="resultStatusAutomationSettings.hm_rating_enabled" class="switch" :disabled="!enabledConfig"></el-switch>
          </div>
          <div class="result-status-item">
            <span class="result-status-text">{{ $t(`${i18nPrefix}.label.resultStatusAutomation.interviewResultEnabled`) }}</span>
            <el-switch v-model="resultStatusAutomationSettings.interview_result_enabled" class="switch" :disabled="!enabledConfig"></el-switch>
          </div>
        </div>
      </div>
    <div class="settings-container birthday-reminder-settings-container" v-if="hasBirthdayNoticePremium">
      <span class="title birthday-reminder-title">{{ $t(`${i18nPrefix}.label.birthdayReminderTitle`) }}</span>
      <div class="birthday-reminder-settings">
        <div class="settings-prev">{{ $t(`${i18nPrefix}.label.birthdayReminderToCandidate`) }}</div>
        <el-switch
          v-model="birthdayReminderSettings.send_to_candidate"
          class="switch"
          :disabled="!enabledConfig"
        ></el-switch>
        <template v-if="hasEmployeePremium">
          <div class="settings-prev">{{ $t(`${i18nPrefix}.label.birthdayReminderToEmployee`) }}</div>
          <el-switch
            v-model="birthdayReminderSettings.send_to_employee"
            class="switch"
            :disabled="!enabledConfig"
          ></el-switch>
        </template>
        <template v-if="hasAlumniPremium">
          <div class="settings-prev">{{ $t(`${i18nPrefix}.label.birthdayReminderToAlumni`) }}</div>
          <el-switch
            v-model="birthdayReminderSettings.send_to_alumni"
            class="switch"
            :disabled="!enabledConfig"
          ></el-switch>
        </template>
      </div>
    </div>
    <div v-if="hasChangeStatusSettingsPermission" class="settings-container status-settings-container">
      <div class="title">
        <span class="label">{{ $t(`${i18nPrefix}.label.changeStatusSettingsTitle`) }}</span>
        <el-switch
            v-model="statusChangeSettings.is_enabled"
            class="switch"
            :disabled="!enabledConfig"
          ></el-switch>
      </div>
      <div class="status-select-list">
        <span class="tip">{{ $t(`${i18nPrefix}.label.changeStatusSettingsTip`) }}</span>
        <el-select v-model="statusChangeSettings.disabled_status_list" class="status-list" multiple filterable clearable size="mini" :disabled="!enabledConfig">
          <el-option
            v-for="(status, index) in statusChangeStatusList"
            :key="index"
            :label="status.i18nName"
            :value="status.key"
          ></el-option>
        </el-select>
      </div>
    </div>

    <div class="settings-container login-settings-container">
      <span class="title login-settings-title">{{ $t(`${i18nPrefix}.label.loginSettingsTitle`) }}</span>
      <div class="login-settings">
        <div class="settings-prev">{{ $t(`${i18nPrefix}.label.loginSettingsSessionAge`) }}</div>
        <el-input-number v-model="loginSettings.session_age" :disabled="!enabledConfig" :min="1" :max="720" class="login-settings-input"></el-input-number>
        <span class="tip">{{ $t(`${i18nPrefix}.label.loginSettingsSessionAgeTip`) }}</span>
      </div>
      <div class="login-settings">
        <div class="settings-prev">{{ $t(`${i18nPrefix}.label.loginSettingsEmployeeAutoWxLogin`) }}</div>
        <el-switch
          v-model="loginSettings.enable_employee_auto_wx_login"
          class="switch"
          :disabled="!enabledConfig"
        ></el-switch>
      </div>
    </div>
    <div class="settings-container application-reminder-settings-container">
      <span class="title">{{ $t(`${i18nPrefix}.label.applicationReminderSettings.title`) }}</span>
      <div class="channel-select-list">
        <span class="content-regarding">{{ $t(`${i18nPrefix}.label.applicationReminderSettings.regarding`) }}</span>
        <el-select v-model="applicationReminderSettings.channel_ids" multiple filterable clearable class="channel-list" size="mini" :disabled="!enabledConfig">
          <el-option
            v-for="(item, index) in sourceChannelOptions"
            :key="index"
            :label="item.i18nName"
            :value="item.id"
          ></el-option>
        </el-select>
        <span class="content-not-send">{{ $t(`${i18nPrefix}.label.applicationReminderSettings.applicationWillNotBeSent`) }}</span>
        <el-select v-model="applicationReminderSettings.message_types" multiple filterable clearable class="channel-list" size="mini" :disabled="!enabledConfig">
          <el-option
            v-for="(item, index) in messageTypeOptions"
            :key="index"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>
        <span class="content-via">{{ $t(`${i18nPrefix}.label.applicationReminderSettings.via`) }}</span>
        <el-select v-model="applicationReminderSettings.send_types" multiple filterable clearable class="channel-list" size="mini" :disabled="!enabledConfig">
          <el-option
            v-for="(item, index) in sendTypeOptions"
            :key="index"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>
      </div>
      <div class="referral-application-hr-notice">
        <span class="title">{{ $t(`${i18nPrefix}.label.applicationReminderSettings.hrNoticeTitle`) }}</span>
        <el-switch v-model="applicationReminderSettings.need_notice_hr" class="switch" :disabled="!enabledConfig"></el-switch>
      </div>
      <div class="referral-application-hr-notice job-member">
        <span class="title">{{ $t(`${i18nPrefix}.label.applicationReminderSettings.jobMemberNoticeTitle`) }}</span>
        <el-switch v-model="applicationReminderSettings.need_notice_job_member" class="switch" :disabled="!enabledConfig"></el-switch>
      </div>
    </div>
    <div v-if="hasAIResumeMatchPremium" class="settings-container ai-settings-container">
      <span class="title">{{ $t(`${i18nPrefix}.label.aiSettings.title`) }}</span>
      <div class="ai-settings">
        <div class="ai-setting-item">
          <span class="ai-setting-text">{{ $t(`${i18nPrefix}.label.aiSettings.candidateAiFitScoreForHmEnabled`) }}</span>
          <el-switch v-model="aiSettings.candidate_ai_fit_score_for_hm_enabled" class="switch" :disabled="!enabledConfig"></el-switch>
        </div>
      </div>
    </div>
    <div class="btn-group">
      <el-button class="save-btn" size="small" @click="handleClickSave">{{ $t('common.btn.save') }}</el-button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty, createI18nProperty } from '@/i18n'
import { validateEmail } from '@/utils/validator'
import { ROOT_COMPANY_ID } from '@/constant/company'

const INTERVIEW_SETTING_OPTIONS = {
  INTERVIEWED: 'INTERVIEWED',
  HN_RATED: 'HN_RATED'
}

const SOURCE_STATUS_MODE = {
  SELECTED: 'SELECTED',
  ALL: 'ALL'
}

const createDefaultApplicationStatusChange = () => ({
  is_enabled: false,
  source_status_mode: SOURCE_STATUS_MODE.SELECTED,
  source_status_keys: [],
  target_status_key: null
})

const createApplicationStatusChange = applicationStatusChange => {
  const setting = {
    ...createDefaultApplicationStatusChange(),
    ...(applicationStatusChange || {})
  }
  setting.source_status_mode = setting.source_status_mode === SOURCE_STATUS_MODE.ALL
    ? SOURCE_STATUS_MODE.ALL
    : SOURCE_STATUS_MODE.SELECTED
  setting.source_status_keys = setting.source_status_mode === SOURCE_STATUS_MODE.ALL
    ? []
    : _.uniq(setting.source_status_keys || [])
  return setting
}

const createJobSettings = jobSettings => {
  const sourceSettings = jobSettings || {}
  return {
    job_close_email_template: null,
    job_close_status: null,
    job_close_setting_is_enabled: false,
    job_cancel_email_template: null,
    job_cancel_status: null,
    job_cancel_setting_is_enabled: false,
    ...sourceSettings,
    job_close_application_status_change: createApplicationStatusChange(sourceSettings.job_close_application_status_change),
    job_cancel_application_status_change: createApplicationStatusChange(sourceSettings.job_cancel_application_status_change)
  }
}

export default {
  data () {
    return {
      i18nPrefix: 'admin.job.editor',
      company: {},
      companyId: null,
      companySearchKeywords: null,
      enabledConfig: false,
      companyList: [],
      jobCloseEmailTemplateList: [],
      jobCancelEmailTemplateList: [],
      candidateStatusList: [],
      search: {
        companyId: null
      },
      isValidateEmail: false,
      errMsg: '',
      jobSettings: createJobSettings(),
      companyPolicySettings: {
        revoked_notice_email: '',
        revoked_need_notice_candidate: false
      },
      interviewSatisfactionSurveySettings: {
        interview_satisfaction_survey_send_type: null
      },
      birthdayReminderSettings: {
        send_to_candidate: false,
        send_to_employee: false,
        send_to_alumni: false
      },
      loginSettings: {
        session_age: null,
        disable_employee_auto_wx_login: false
      },
      applicationReminderSettings: {
        channel_ids: [],
        message_types: [],
        send_types: [],
        need_notice_hr: false,
        need_notice_job_member: false
      },
      resultStatusAutomationSettings: {
        hm_rating_enabled: false,
        interview_result_enabled: false
      },
      aiSettings: {
        candidate_ai_fit_score_for_hm_enabled: true
      },
      originalResponseData: {},
      statusChangeStatusList: [],
      statusChangeSettings: {
        is_enabled: false,
        disabled_status_list: []
      },
      sourceChannelOptions: []
    }
  },
  computed: {
    isEn () {
      return this.$i18n.locale === 'en'
    },
    hasPolicyPermission () {
      return this.hasAnyPermission('PERM_HR_COMPANY_POLICY_MANAGEMENT')
    },
    hasSatisfactionSurveyPermission () {
      return this.hasAnyPermission('PERM_SATISFACTION_SURVEY_MANAGE')
    },
    hasSatisfactionSurveyPremium () {
      return this.hasAnyPermission('PREMIUM_SATISFACTION_SURVEY')
    },
    hasEmployeePremium () {
      return this.hasAnyPermission('COMPUTED_COMPANY_REFERRAL_ENABLED')
    },
    hasAlumniPremium () {
      return this.hasAnyPermission('PREMIUM_ALUMNI_MANAGEMENT')
    },
    hasAIResumeMatchPremium () {
      return this.hasAnyPermission('PREMIUM_AI_RESUME_MATCH')
    },
    hasBirthdayNoticePremium () {
      return this.hasAnyPermission('PREMIUM_BIRTHDAY_NOTICE')
    },
    hasChangeStatusSettingsPermission () {
      return this.hasAnyPermission(['PREMIUM_NON_CONSENT_CANDIDATE_CHANGE_STATUS_LOGIC', 'PERM_NON_CONSENT_CANDIDATE_CHANGE_STATUS_LOGIC'])
    },
    searchedCompanyList () {
      if (this.companySearchKeywords) {
        return _.filter(this.companyList, company => _.includes(company.i18nName.toUpperCase(), this.companySearchKeywords.toUpperCase()))
      } else {
        return this.companyList
      }
    },
    interviewSettingOption () {
      return _.map(INTERVIEW_SETTING_OPTIONS, (val, key) => {
        return {
          label: this.$t(`${this.i18nPrefix}.interviewSatisfactionSettingOption.${key}`),
          value: val
        }
      })
    },
    isMSDCompany () {
      return _.includes([ROOT_COMPANY_ID.MSD, ROOT_COMPANY_ID.MSD_OLD], this.company?.id)
    },
    messageTypeOptions () {
      return [
        { label: this.$t('admin.job.editor.label.applicationReminderSettings.continueToCompleteTheConsent'), value: 'Acquire Candidate Consent' },
        { label: this.$t('admin.job.editor.label.applicationReminderSettings.continueToCompleteTheApplication'), value: 'Incomplete Application Reminder' }
      ]
    },
    sendTypeOptions () {
      return [
        { label: this.$t('admin.job.editor.label.applicationReminderSettings.email'), value: 'Email' },
        { label: this.$t('admin.job.editor.label.applicationReminderSettings.sms'), value: 'SMS' },
        { label: this.$t('admin.job.editor.label.applicationReminderSettings.weChat'), value: 'WeChat' }
      ]
    },
    jobCloseApplicationSourceStatusSelection: {
      get () {
        const setting = this.jobSettings.job_close_application_status_change
        const availableStatusKeys = _.map(this.jobCloseApplicationSourceStatusOptions, 'key')
        if (setting.source_status_mode === SOURCE_STATUS_MODE.ALL) {
          return availableStatusKeys
        }
        return _.intersection(_.uniq(setting.source_status_keys || []), availableStatusKeys)
      },
      set (statusKeys) {
        const availableStatusKeys = _.map(this.jobCloseApplicationSourceStatusOptions, 'key')
        const sourceStatusKeys = _.intersection(_.uniq(statusKeys || []), availableStatusKeys)
        const setting = this.jobSettings.job_close_application_status_change
        const hasSelectedAllStatus = !_.isEmpty(availableStatusKeys) && sourceStatusKeys.length === availableStatusKeys.length
        if (hasSelectedAllStatus) {
          setting.source_status_mode = SOURCE_STATUS_MODE.ALL
          setting.source_status_keys = []
          return
        }
        setting.source_status_mode = SOURCE_STATUS_MODE.SELECTED
        setting.source_status_keys = sourceStatusKeys
      }
    },
    jobCloseApplicationSourceStatusOptions () {
      const targetStatusKey = this.jobSettings.job_close_application_status_change.target_status_key
      return _.filter(this.statusChangeStatusList, status => status.key !== targetStatusKey)
    },
    jobCloseApplicationTargetStatusOptions () {
      const setting = this.jobSettings.job_close_application_status_change
      if (setting.source_status_mode === SOURCE_STATUS_MODE.ALL) {
        return this.statusChangeStatusList
      }
      return _.filter(this.statusChangeStatusList, status => !_.includes(setting.source_status_keys, status.key))
    },
    jobCancelApplicationSourceStatusSelection: {
      get () {
        const setting = this.jobSettings.job_cancel_application_status_change
        const availableStatusKeys = _.map(this.jobCancelApplicationSourceStatusOptions, 'key')
        if (setting.source_status_mode === SOURCE_STATUS_MODE.ALL) {
          return availableStatusKeys
        }
        return _.intersection(_.uniq(setting.source_status_keys || []), availableStatusKeys)
      },
      set (statusKeys) {
        const availableStatusKeys = _.map(this.jobCancelApplicationSourceStatusOptions, 'key')
        const sourceStatusKeys = _.intersection(_.uniq(statusKeys || []), availableStatusKeys)
        const setting = this.jobSettings.job_cancel_application_status_change
        const hasSelectedAllStatus = !_.isEmpty(availableStatusKeys) && sourceStatusKeys.length === availableStatusKeys.length
        if (hasSelectedAllStatus) {
          setting.source_status_mode = SOURCE_STATUS_MODE.ALL
          setting.source_status_keys = []
          return
        }
        setting.source_status_mode = SOURCE_STATUS_MODE.SELECTED
        setting.source_status_keys = sourceStatusKeys
      }
    },
    jobCancelApplicationSourceStatusOptions () {
      const targetStatusKey = this.jobSettings.job_cancel_application_status_change.target_status_key
      return _.filter(this.statusChangeStatusList, status => status.key !== targetStatusKey)
    },
    jobCancelApplicationTargetStatusOptions () {
      const setting = this.jobSettings.job_cancel_application_status_change
      if (setting.source_status_mode === SOURCE_STATUS_MODE.ALL) {
        return this.statusChangeStatusList
      }
      return _.filter(this.statusChangeStatusList, status => !_.includes(setting.source_status_keys, status.key))
    }
  },
  async created () {
    await this.getCompanyList()
    this.loadCompanyEmailTemplate()
    this.loadSourceChannelList()
  },
  mounted () {
    window.addEventListener('beforeunload', this.beforeUnloadHandler, false)
  },
  destroyed () {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler, false)
  },
  methods: {
    ...mapActions('company', ['getCompanyChildrenTree', 'createCompanyAutomationSettings', 'getCompanyAutomationSettings']),
    ...mapActions('notificationTemplate', ['getCompanyEmailTemplateByType']),
    ...mapActions('candidateStatus', ['getCandidateStatusList']),
    ...mapActions('sourceCode', ['getSourceChannelList']),
    beforeUnloadHandler (e) {
      if (this.validateHasChangedValue()) {
        // 必须要这段代码阻止默认行为，提示的弹窗才会出来
        event.preventDefault()
        e.returnValue = ''
      }
    },
    validateHasChangedValue () {
      if (!this.originalResponseData) {
        return false
      }
      const originalData = this.originalResponseData
      if (!_.isEqual(_.sortBy(originalData.status_change_settings.disabled_status_list), _.sortBy(this.statusChangeSettings.disabled_status_list))) {
        return true
      }
      const newData = {
        is_enabled: this.enabledConfig,
        job_settings: this.jobSettings,
        company_policy_settings: this.companyPolicySettings,
        interview_satisfaction_survey_settings: this.interviewSatisfactionSurveySettings,
        birthday_reminder_settings: this.birthdayReminderSettings,
        status_change_settings: this.statusChangeSettings,
        login_settings: this.loginSettings,
        application_reminder_settings: this.applicationReminderSettings,
        candidate_status_automation_settings: this.resultStatusAutomationSettings,
        ai_settings: this.aiSettings
      }
      let isChange = false
      _.forEach(originalData, (oldSettings, key) => {
        if (isChange) {
          return
        }
        const newSettings = _.get(newData, key)
        if (key === 'status_change_settings') {
          isChange = oldSettings.is_enabled !== newSettings.is_enabled
          return
        }
        isChange = !_.isEqual(oldSettings, newSettings)
      })
      return isChange
    },
    validateEmailAddress () {
      if (!this.companyPolicySettings.revoked_notice_email || this.companyPolicySettings.revoked_notice_email === '') {
        this.isValidateEmail = true
        return
      }
      const errMsg = validateEmail(null, this.companyPolicySettings.revoked_notice_email)
      if (errMsg) {
        this.isValidateEmail = false
        this.errMsg = errMsg
      } else {
        this.isValidateEmail = true
      }
    },
    handleFilterCompany (keywords) {
      this.companyId = keywords
      this.companySearchKeywords = keywords
    },
    handleVisibilityChange (isVisibility) {
      if (isVisibility && this.companySearchKeywords) {
        this.companyId = this.companySearchKeywords
      } else {
        this.companySearchKeywords = null
        this.companyId = this.search.companyId
      }
    },
    handleCompanySelectStyle (company) {
      return { 'padding-left': `${(company.level || 1) * 15}px` }
    },
    formatCompanyData (data, company, companyList) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      companyList.push(company)
      const allChildren = data.children
      const children = allChildren[company.id]
      if (children && children.length > 0) {
        for (const child of children) {
          this.formatCompanyData(data, child, companyList)
        }
      }
      return companyList
    },
    async getCompanyList (companyId) {
      this.$eventBus.$emit('showLoading', true)
      try {
        const companyTreeResp = await this.getCompanyChildrenTree()
        const currentCompany = {
          id: companyTreeResp.self.id,
          name: companyTreeResp.self.name,
          cn_name: companyTreeResp.self.cn_name,
          level: companyTreeResp.self.level
        }
        this.company = currentCompany
        const companyList = []
        this.companyList = this.formatCompanyData(companyTreeResp, currentCompany, companyList)
        if (!companyId) {
          this.search.companyId = this.company.id
          this.companyId = this.company.id
        } else {
          this.search.companyId = companyId
          this.companyId = companyId
        }
        await this.searchCompanyList()
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async searchCompanyList () {
      this.search.companyId = this.companyId
      if (!this.search.companyId) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      this.loadCandidateStatusList()
      try {
        await this.loadCompanyAutomationSettings()
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadCompanyAutomationSettings () {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.getCompanyAutomationSettings({ companyId: this.search.companyId })
        if (resp) {
          this.jobSettings = createJobSettings(resp.job_settings)
          if (resp.company_policy_settings) {
            this.companyPolicySettings = resp.company_policy_settings
          } else {
            this.companyPolicySettings = {
              revoked_notice_email: '',
              revoked_need_notice_candidate: false
            }
          }
          if (resp.interview_satisfaction_survey_settings) {
            this.interviewSatisfactionSurveySettings = resp.interview_satisfaction_survey_settings
          } else {
            this.interviewSatisfactionSurveySettings = {
              interview_satisfaction_survey_send_type: 'HN_RATED'
            }
          }
          if (resp.birthday_reminder_settings) {
            this.birthdayReminderSettings = resp.birthday_reminder_settings
          } else {
            this.birthdayReminderSettings = {
              send_to_candidate: false,
              send_to_employee: false,
              send_to_alumni: false
            }
          }
          this.enabledConfig = resp.is_enabled
          if (resp.status_change_settings) {
            this.statusChangeSettings = resp.status_change_settings
          } else {
            this.statusChangeSettings = {
              is_enabled: false,
              disabled_status_list: []
            }
          }

          this.loginSettings = {
            session_age: resp.login_settings?.session_age || 4,
            enable_employee_auto_wx_login: !resp.login_settings?.disable_employee_auto_wx_login
          }

          if (resp.application_reminder_settings) {
            this.applicationReminderSettings = resp.application_reminder_settings
          } else {
            this.applicationReminderSettings = {
              channel_ids: [],
              message_types: [],
              send_types: [],
              need_notice_hr: false,
              need_notice_job_member: false
            }
          }
          this.resultStatusAutomationSettings = {
            hm_rating_enabled: _.get(resp, 'candidate_status_automation_settings.hm_rating_enabled', false),
            interview_result_enabled: _.get(resp, 'candidate_status_automation_settings.interview_result_enabled', false)
          }
          this.aiSettings = {
            candidate_ai_fit_score_for_hm_enabled: _.get(resp, 'ai_settings.candidate_ai_fit_score_for_hm_enabled', true)
          }
          this.originalResponseData = _.cloneDeep({
            ...resp,
            job_settings: this.jobSettings,
            candidate_status_automation_settings: this.resultStatusAutomationSettings,
            ai_settings: this.aiSettings
          })
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadCompanyEmailTemplate () {
      const jobCloseResp = await this.getCompanyEmailTemplateByType({ email_template_type: 'Notice to Candidates - Job Now Closed', has_multiple: 1 })
      const jobCancelResp = await this.getCompanyEmailTemplateByType({ email_template_type: 'Notice to Candidates - Job Now Cancelled', has_multiple: 1 })
      if (jobCloseResp) {
        this.jobCloseEmailTemplateList = jobCloseResp
      }

      if (jobCancelResp) {
        this.jobCancelEmailTemplateList = jobCancelResp
      }
    },
    async loadCandidateStatusList () {
      const resp = await this.getCandidateStatusList({ is_all: 1, company_id: this.search.companyId })
      if (resp) {
        this.candidateStatusList = _.map(resp, item => addI18nProperty(item, 'i18nName', 'name_cn', 'name_en'))
        this.statusChangeStatusList = _.chain(this.candidateStatusList).filter(item => this.isMSDCompany ? !['Hired', 'Disqualified'].includes(item.key) : true).filter(item => !item.is_hidden).value()
      }
      return this.candidateStatusList
    },
    async loadSourceChannelList () {
      const params = {
        status: 1,
        global_channel: 1,
        company_id: this.companyId
      }
      const resp = await this.getSourceChannelList(params)
      if (resp) {
        const weChatChannel = createI18nProperty({ id: 0 }, 'i18nName', `${this.i18nPrefix}.label.applicationReminderSettings.weChat`)
        const channelList = _.map(resp.result, item => addI18nProperty(item, 'i18nName', 'cn_name', 'name'))
        channelList.splice(0, 0, weChatChannel)
        this.sourceChannelOptions = channelList
      }
    },
    validateApplicationStatusChange (setting, messageKeyPrefix) {
      if (!this.enabledConfig || !setting.is_enabled) {
        return true
      }
      const availableStatusKeys = _.map(this.statusChangeStatusList, 'key')
      if (!setting.target_status_key) {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.${messageKeyPrefix}TargetStatusRequired`))
        return false
      }
      if (!_.includes(availableStatusKeys, setting.target_status_key)) {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.${messageKeyPrefix}StatusInvalid`))
        return false
      }
      if (setting.source_status_mode === SOURCE_STATUS_MODE.SELECTED) {
        if (_.isEmpty(setting.source_status_keys)) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.${messageKeyPrefix}SourceStatusRequired`))
          return false
        }
        const hasInvalidSourceStatus = _.some(setting.source_status_keys, statusKey => !_.includes(availableStatusKeys, statusKey))
        if (hasInvalidSourceStatus || _.includes(setting.source_status_keys, setting.target_status_key)) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.${messageKeyPrefix}StatusInvalid`))
          return false
        }
      }
      return true
    },
    validateJobCloseApplicationStatusChange () {
      return this.validateApplicationStatusChange(this.jobSettings.job_close_application_status_change, 'jobCloseApplication')
    },
    validateJobCancelApplicationStatusChange () {
      return this.validateApplicationStatusChange(this.jobSettings.job_cancel_application_status_change, 'jobCancelApplication')
    },
    async handleClickSave () {
      if (!this.isValidateEmail && this.errMsg) {
        return
      }
      if (!this.validateJobCloseApplicationStatusChange()) {
        return
      }
      if (!this.validateJobCancelApplicationStatusChange()) {
        return
      }
      const settingParams = {
        company_id: this.search.companyId,
        enabled_config: this.enabledConfig,
        job_settings: _.cloneDeep(this.jobSettings),
        company_policy_settings: this.companyPolicySettings,
        interview_satisfaction_survey_settings: this.interviewSatisfactionSurveySettings,
        birthday_reminder_settings: this.birthdayReminderSettings,
        status_change_settings: this.statusChangeSettings,
        login_settings: {
          session_age: this.loginSettings.session_age,
          disable_employee_auto_wx_login: !this.loginSettings.enable_employee_auto_wx_login
        },
        application_reminder_settings: this.applicationReminderSettings,
        candidate_status_automation_settings: this.resultStatusAutomationSettings,
        ai_settings: this.aiSettings
      }
      try {
        const resp = await this.createCompanyAutomationSettings(settingParams)
        if (resp) {
          this.$message({
            type: 'success',
            message: this.$t(`${this.i18nPrefix}.message.saveJobSettingsSuccess`)
          })
        }
      } finally {
        await this.searchCompanyList()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.automation-settings {
  overflow: hidden;
  padding: 28px 24px 30px 24px;
  background: #FFFFFF;

  ::v-deep {
    .el-input__inner {
      height: 32px;
    }

    .el-input-number {
      height: 32px;
      line-height: 32px;
    }

    .el-input-number__increase, .el-input-number__decrease {
      height: 30px;
    }
  }

  .automation-setting-header {
    display: block;
    height: 32px;

    .header {
      display: inline-block;
      height: 32px;
      .automation-settings-title {
        font-size: 20px;
        color: #303133;
        font-weight: 500;
        vertical-align: middle;
      }

      .need-config-switch {
        line-height: 32px;
        margin-top: 1px;
      }
    }

    .company-select {
      width: 240px;
      float: right;
      ::v-deep {
        .el-input__inner {
          font-size: 14px;
        }
      }
    }

  }

  .divider {
    margin: 18px 0px 30px 0px;
  }

  .settings-container {
    > .title {
      display: block;
      font-size: 18px;
      margin-top: 24px;
      color: #303133;
      font-weight: 500;
    }
  }

  .job-settings-container {
    .job-close-settings {
      margin-top: 24px;
      .title {
        .title-content {
          margin-right: 16px;
          color: #606266;
        }
      }

      .job-close-content {
        display: flex;
        align-items: center;
        margin-top: 22px;
        .email-template-select {
          width: 15%;
          ::v-deep {
            .el-input__inner {
              font-size: 14px;
              padding-right: 30px;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }

        .status-step-select {
          width: 15%;
          ::v-deep {
            .el-input__inner {
              font-size: 14px;
            }
          }
        }

        .content-prev {
          margin-right: 14px;
          color: #606266;
        }

        .email-to {
          margin: 0px 14px;
          color: #606266;
        }

        .content-end {
          margin-left: 14px;
          color: #606266;
        }

      }
    }

    .job-cancel-settings {
      margin-top: 24px;
      .title {
        .title-content {
          margin-right: 16px;
          color: #606266;
        }
      }

      .job-cancel-content {
        margin-top: 22px;
        display: flex;
        align-items: center;
        .email-template-select {
          width: 15%;
          ::v-deep {
            .el-input__inner {
              font-size: 14px;
              padding-right: 30px;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }

        .status-step-select {
          width: 15%;
          ::v-deep {
            .el-input__inner {
              font-size: 14px;
            }
          }
        }

        .content-prev {
          margin-right: 14px;
          color: #606266;
        }

        .email-to {
          margin: 0px 14px;
          color: #606266;
        }

        .content-end {
          margin-left: 14px;
          color: #606266;
        }
      }
    }

    .job-close-application-status-settings {
      margin-top: 24px;

      .title-content {
        margin-right: 16px;
        color: #606266;
      }

      .job-close-application-status-content {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        margin-top: 22px;

        .content-label {
          margin-right: 14px;
          color: #606266;
        }

        .application-status-select {
          width: 15%;
        }

        .target-status-label {
          margin: 0 14px;
        }

      }

      .job-close-application-status-content + .job-close-application-status-content {
        margin-top: 16px;
      }
    }
  }

  .company-policy-settings-container {

    .revoke-email-address {
      margin-top: 24px;
      .email-title {
        display: flex;
        align-items: center;
        .title {
          margin-right: 14px;
          color: #606266;
        }

        .email-input {
          width: 24%;
        }

        .validate-required {
          color: #F56C6C;
          font-size: 12px;
          margin-left: 14px;
        }
      }

      .switch-send-candidate {
        margin-top: 24px;
        .switch-text {
          margin-right: 14px;
          color: #606266;
        }
      }
    }
  }

  .interview-satisfaction-settings-container {

    .interview-satisfaction-settings {
      display: flex;
      align-items: center;
      margin-top: 24px;
      .settings-prev {
        margin-right: 14px;
        color: #606266;
      }

      .interview-satisfaction-setting-select {
        width: 15%;
        ::v-deep {
          .el-input__inner {
            font-size: 14px;
          }
        }
      }
    }
  }

  .result-status-automation-settings-container {
    .result-status-automation-list {
      margin-top: 24px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .result-status-item {
      margin-top: 16px;
      display: flex;
      align-items: center;

      &:first-child {
        margin-top: 0;
      }

      .result-status-text {
        margin-right: 14px;
        line-height: 20px;
        color: #606266;
      }
    }
  }

  .ai-settings-container {
    .ai-settings {
      margin-top: 24px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .ai-setting-item {
      display: flex;
      align-items: center;

      .ai-setting-text {
        margin-right: 14px;
        line-height: 20px;
        color: #606266;
      }
    }
  }

  .birthday-reminder-settings-container {
    .birthday-reminder-settings {
      margin-top: 24px;
      display: flex;
      align-items: center;
      .settings-prev {
        line-height: 20px;
        height: 20px;
        color: #606266;
      }

      .switch {
        margin: 0 14px;
      }

    }
  }

  .status-settings-container {
    .title {
      .label {
        margin-right: 16px;
      }
      .switch {
        vertical-align: sub;
      }
    }
    .status-select-list {
      margin-top: 24px;
      .tip {
        margin-right: 14px;
        color: #606266;
      }
      .status-list {
        width: 20%;
      }
    }
  }

  .login-settings-container {
    .login-settings {
      display: flex;
      align-items: center;
      margin-top: 24px;
      .settings-prev {
        margin-right: 14px;
        color: #606266;
      }

      // .login-settings-input {
      //   width: 8%;
      // }

      .tip {
        margin-left: 14px;
      }
    }
  }

  .application-reminder-settings-container {
    .channel-select-list {
      margin-top: 24px;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      row-gap: 20px;
      .content-regarding {
        margin-right: 14px;
        color: #606266;
      }
      .channel-list {
        width: 20%;
      }
      .content-not-send {
        margin-left: 14px;
        margin-right: 14px;
        color: #606266;
      }
      .content-via {
        margin-left: 14px;
        margin-right: 14px;
        color: #606266;
      }
    }
    .referral-application-hr-notice {
      margin-top: 20px;
      display: flex;
      align-items: center;
      .title {
        font-size: 14px;
        color: #606266;
      }
      .switch {
        margin-left: 12px;
      }
    }
  }

  .btn-group {
    margin-top: 66px;
    float: right;

    .save-btn {
      background: #0096CC;
      font-size: 16px;
      font-weight: 500;
      color: #FFFFFF;
      width: 88px;
      height: 40px;
    }
  }
}
</style>
